<template>
    <hox-notification-item
        :show-button="isCompleted"
        :show-clear-button="showClearButton"
        :show-error-message="hasFailed"
        :show-abort-button="showAbortButton"
        :status="notification.status"
        :notification="notification"
        :is-job-finished="isJobFinished"
        @clear="removeNotification"
        @notificationDeleted="deleteCompletedNotification"
    >
        <template #title>
            <span v-if="notification.job.campaignDetails && notification.job.clientDetails">
                Client:
                <strong>{{ notification.job.clientDetails?.name }}</strong>
                <br />
                Campaign:
                <strong>{{ notification.job.campaignDetails?.name }}</strong>
            </span>
            {{ title }}
        </template>
        <template #subtitle>
            {{ statusText }}
        </template>
        <template #content>
            <template v-if="isCompleted">
                <p>Here is the download. Please press the button to download your deliverables.</p>
            </template>
        </template>
        <template #errorMessage>
            <p>There was an unexpected issue while preparing ads for download.</p>
        </template>
        <template #button>
            <Button size="small" target="_blank" @click="downloadHandler">
                <Icon type="md-download" data-testid="notification-item-download-icon" />
            </Button>
        </template>
    </hox-notification-item>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import nl2br from "hoxton-shared/utils/nl2br";

import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { IndesignJobPlatform } from "@/enums/jobs";

import { NotificationsAction, type Notification } from "@/store/modules/notifications";
import { resourceManagerRequest } from "@/utils/CreativeIntelligenceApi";
import { fetchAndDownloadFile } from "@/utils/downloadFile";

type FileLocationWithDownloadUrl = { downloadUrl?: string; signedUrl?: string };
type FileLocations = Record<string, FileLocationWithDownloadUrl | string>;

export default defineComponent({
    props: {
        notification: {
            required: true,
            type: Object as PropType<Notification>
        },

        showAbortButton: {
            type: Boolean
        },

        isJobFinished: {
            type: Boolean,
            default: false
        }
    },

    emits: ["notificationDeleted"],

    computed: {
        hasFailed() {
            return this.notification.status === NotificationStatus.Failed;
        },

        isCompleted() {
            return this.notification.status === NotificationStatus.Completed;
        },

        notificationMessage() {
            return nl2br(this.notification.message);
        },

        showClearButton() {
            return !this.isJobFinished && (this.hasFailed || this.isCompleted);
        },

        statusText() {
            if (this.hasFailed) {
                return "Failed";
            }
            return this.notificationMessage;
        },

        isExcelJob() {
            return this.notification.type === NotificationTypes.FlashtalkingDownloadExcel;
        },

        isAssetsJob() {
            return this.notification.type === NotificationTypes.FlashtalkingDownloadAssets;
        },

        isPsdJob() {
            return this.notification.type === NotificationTypes.PsdDownloader;
        },

        isAEJob() {
            return this.notification.type === NotificationTypes.PlainlyBatchRender;
        },

        isIndesignJob() {
            return this.notification.type === NotificationTypes.AdobeIndesignDownload;
        },

        title() {
            if (this.isExcelJob) {
                return "Download your Excel";
            }

            if (this.isAssetsJob) {
                return "Download your Assets";
            }

            if (this.isPsdJob) {
                return "Download your PSD ads";
            }

            if (this.isAEJob) {
                return "Download your After Effects videos";
            }

            if (this.isIndesignJob) {
                let format;

                switch (this.notification.platform as (typeof IndesignJobPlatform)[keyof typeof IndesignJobPlatform]) {
                    case IndesignJobPlatform.INDESIGN_PDF:
                        format = "pdf";
                        break;
                    case IndesignJobPlatform.INDESIGN_JPG:
                        format = "jpg";
                        break;
                    case IndesignJobPlatform.INDESIGN_INDD:
                        format = "indd";
                        break;
                    case IndesignJobPlatform.INDESIGN_BUNDLE:
                        format = "bundle";
                        break;
                }

                return `Download your InDesign files (${format})`;
            }

            return "";
        }
    },

    methods: {
        removeNotification() {
            this.$store.dispatch(NotificationsAction.RemoveItem, this.notification.id);
        },

        getDownloadUrlFromFileLocation(fileLocation: FileLocationWithDownloadUrl | string | undefined) {
            if (typeof fileLocation === "string") {
                return fileLocation;
            }
            return fileLocation?.downloadUrl || fileLocation?.signedUrl;
        },

        getDownloadUrlFromFileLocations(fileLocations: FileLocations | undefined) {
            if (!fileLocations) {
                return undefined;
            }

            const fileLocationKeys = [
                "excelFileLocation",
                "zipFileLocation",
                "assetsFileLocation",
                "fileLocation",
                "renderResultFileLocation",
                "zipUrl"
            ];
            for (const key of fileLocationKeys) {
                const downloadUrl = this.getDownloadUrlFromFileLocation(fileLocations[key]);
                if (downloadUrl) {
                    return downloadUrl;
                }
            }
            return undefined;
        },

        getLinkLabel(link: string) {
            try {
                const url = new URL(link);
                const segments = url.pathname.split("/").filter(Boolean);
                const lastSegment = segments[segments.length - 1];

                if (lastSegment) {
                    return decodeURIComponent(lastSegment);
                }
            } catch {
                return "Untitled file";
            }

            return "Untitled file";
        },

        async downloadHandler() {
            try {
                const body = {
                    type: this.notification.type,
                    input: this.notification.job.payload
                };

                const response = await resourceManagerRequest({
                    id: this.notification.job._id,
                    body
                });

                const downloadUrl = this.getDownloadUrlFromFileLocations(response?.data?.output?.success);

                if (downloadUrl) {
                    await fetchAndDownloadFile(downloadUrl, this.getLinkLabel(downloadUrl));
                }
            } catch (e) {
                console.log(e);
            }
        },

        deleteCompletedNotification() {
            this.$emit("notificationDeleted");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-job-notification-item__sub-progress {
    margin-top: $spacing-small;
    padding-left: $spacing-base;
    border-left: 1px solid $cmp-light-border-color;

    &-label {
        display: flex;
        justify-content: space-between;
        overflow: hidden;
        margin-bottom: $spacing-smaller;

        > :first-child {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}
</style>
